import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { fetchEnablementResources, completeResource, uncompleteResource, fetchEnablementProgress } from '@/api/enablement'
import { Check, ExternalLink, BookOpen, Video, FileText, Link as LinkIcon, Loader2 } from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'
import { toast } from 'sonner'

const typeIcons = {
  video: Video,
  document: FileText,
  course: BookOpen,
  link: LinkIcon
}

export function Enablement() {
  const queryClient = useQueryClient()
  
  const { data: resourcesData, isLoading: isLoadingResources } = useQuery({
    queryKey: ['enablement-resources'],
    queryFn: fetchEnablementResources,
  })
  
  const { data: progressData, isLoading: isLoadingProgress } = useQuery({
    queryKey: ['enablement-progress'],
    queryFn: fetchEnablementProgress,
  })

  const completeMutation = useMutation({
    mutationFn: completeResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enablement-resources'] })
      queryClient.invalidateQueries({ queryKey: ['enablement-progress'] })
      toast.success('Marked as complete')
    },
  })

  const uncompleteMutation = useMutation({
    mutationFn: uncompleteResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enablement-resources'] })
      queryClient.invalidateQueries({ queryKey: ['enablement-progress'] })
      toast.success('Marked as incomplete')
    },
  })

  if (isLoadingResources || isLoadingProgress) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse rounded-lg bg-muted" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  const { grouped = {} } = resourcesData || {}
  const { percentage = 0, completed = 0, total = 0 } = progressData || {}

  const roles = Object.keys(grouped)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Enablement Resources</h1>
          <p className="text-muted-foreground">
            Learn and grow with Cloudflare partner resources
          </p>
        </div>
        <Card className="sm:w-72">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm font-bold">{percentage}%</span>
            </div>
            <Progress value={percentage} className="mt-2" />
            <p className="mt-1 text-xs text-muted-foreground">
              {completed} of {total} resources completed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Role Tabs */}
      <Tabs defaultValue={roles[0]} className="space-y-4">
        <TabsList className="flex flex-wrap">
          {roles.map((role) => (
            <TabsTrigger key={role} value={role}>
              {role}
            </TabsTrigger>
          ))}
        </TabsList>

        {roles.map((role) => (
          <TabsContent key={role} value={role}>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {grouped[role].map((resource: any) => {
                const Icon = typeIcons[resource.type as keyof typeof typeIcons] || BookOpen
                const isCompleted = resource.is_completed

                return (
                  <Card 
                    key={resource.id} 
                    className={cn(
                      'transition-colors',
                      isCompleted ? 'bg-muted/30' : 'card-hover'
                    )}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cloudflare-orange/10">
                          <Icon className="h-5 w-5 text-cloudflare-orange" />
                        </div>
                        {isCompleted && (
                          <Badge className="bg-green-500">
                            <Check className="mr-1 h-3 w-3" />
                            Completed
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                      <CardDescription className="capitalize">
                        {resource.type} • {resource.role}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          asChild
                        >
                          <a href={resource.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open
                          </a>
                        </Button>
                        {isCompleted ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => uncompleteMutation.mutate(resource.id)}
                            disabled={uncompleteMutation.isPending}
                          >
                            {uncompleteMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Undo'
                            )}
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => completeMutation.mutate(resource.id)}
                            disabled={completeMutation.isPending}
                          >
                            {completeMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Complete
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
